/**
 * One sheet, filling the screen.
 *
 * The file a colleague could not open in Excel had a million rows. The point
 * of this page is that the row count stops mattering: the browser is never
 * sent the file. It is converted once into Parquet in object storage, and each
 * page here is a single query reading just that page's bytes, so a
 * ten-million-row file costs the same to open as a ten-thousand-row one.
 *
 * Paging rather than infinite scroll, deliberately. A scrollbar over ten
 * million rows is a control with no useful resolution — one pixel is thousands
 * of rows — so it promises precision it cannot deliver. A row number you can
 * type goes exactly where it says.
 *
 * This is the viewer. Editing lands next, as an overlay of changed cells
 * rather than a rewrite of the file.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ActionIcon, Alert, Anchor, Badge, Box, Button, Group, Loader, Modal, Select, Stack, Text,
  TextInput, Textarea, Tooltip,
} from '@mantine/core';
import {
  IconArrowBackUp, IconArrowForwardUp,
  IconArrowLeft, IconDatabaseImport, IconDeviceFloppy, IconDownload, IconPencilOff,
  IconFilter, IconFilterOff, IconPencil, IconScissors, IconSum, IconTrash, IconX,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useCommitSheet, useDeleteSheetTransform, useDiscardSheetEdits, useSaveSheetEdits,
  useDeleteSheetRows, useReorderSheetColumns, useSheet, useSheetRows, useSheetStats, useUpdateSheet,
  useEntities, useSheetTransforms, type SheetCell, type SheetFilter, type SheetView,
} from '../api/queries';
import { SheetColumnMenu } from './SheetColumnMenu';
import { SheetImportModal } from './SheetImportModal';
import { NewFileFields, fileStem, useNewFileDetails, useOpenNewFile } from './NewFileFields';
import { SheetCellMenu, type CellMenuTarget } from './SheetCellMenu';
import { SheetSelectionBar } from './SheetSelectionBar';
import { SheetGrid, type Change, type GridCell } from './SheetGrid';
import { SheetTotalsBar } from './SheetTotalsBar';
import { SheetComputedModal } from './SheetComputedModal';
import { SheetFormulaModal } from './SheetFormulaModal';
import { SheetFormulaBar } from './SheetFormulaBar';
import { SheetSplitModal } from './SheetSplitModal';
import { SheetSuppressModal } from './SheetSuppressModal';
import { downloadSheet } from '../api/upload';
import { formatNumber } from '../theme';
import { useVocab } from '../vocab';


/** A filter in words, for the same reason as a rule. */
/**
 * A filter in the words the person who set it would use.
 *
 * Every operator gets a case. This used to fall through to "contains" for
 * anything it did not name, which made seven of the thirteen operators
 * describe themselves as something they are not: a chip reading
 * `IsNewToNetwork contains "0"` over rows that all held 1, and
 * `Email contains ""` for the check that finds broken addresses. A filter
 * that misreports itself is worse than one with no label, because the grid
 * looks wrong rather than the chip.
 *
 * Comparisons leave their value unquoted, since it is a number rather than
 * text to match.
 */
export function describeFilter(f: SheetFilter): string {
  switch (f.op) {
    case 'empty': return `${f.col} is empty`;
    case 'not_empty': return `${f.col} is not empty`;
    case 'eq': return `${f.col} is "${f.value}"`;
    case 'ne': return `${f.col} is not "${f.value}"`;
    case 'starts': return `${f.col} starts with "${f.value}"`;
    case 'contains': return `${f.col} contains "${f.value}"`;
    case 'gt': return `${f.col} is more than ${f.value}`;
    case 'gte': return `${f.col} is at least ${f.value}`;
    case 'lt': return `${f.col} is less than ${f.value}`;
    case 'lte': return `${f.col} is at most ${f.value}`;
    case 'between': return `${f.col} is between ${f.value} and ${f.value2}`;
    case 'bad_email': return `${f.col} is not a valid email`;
    case 'bad_phone': return `${f.col} is not a usable UK phone number`;
    /* An operator the server knows and this does not. Say so vaguely rather
       than confidently wrongly. */
    default: return `${f.col} matches "${f.value}"`;
  }
}

/** A filter's identity, so the same one twice is not two chips. */
export function filterKey(f: SheetFilter): string {
  return [f.col, f.op, f.value ?? '', f.value2 ?? ''].join('\u0000');
}

/** Whether a filter removes values rather than picking one. */
function excludes(f: SheetFilter): boolean {
  return f.op === 'ne' || f.op === 'not_empty';
}

/**
 * Add filters to the ones already set.
 *
 * Exclusions pile up; everything else replaces what was on its column.
 *
 * Excluding the blanks and then excluding "no" is two answers to the same
 * question and both are meant — that is how a column gets narrowed down, one
 * unwanted value at a time. Replacing there threw the first answer away and
 * looked like the filter had failed to apply. Two inclusions are the other
 * case: `is "yes"` and `is "no"` together match nothing, so the second is what
 * was meant and it takes the place of the first.
 */
export function mergeFilters(existing: SheetFilter[], incoming: SheetFilter[]): SheetFilter[] {
  const kept = existing.filter(
    (x) => !incoming.some((f) => f.col === x.col && !(excludes(f) && excludes(x))),
  );
  const seen = new Set(kept.map(filterKey));
  return [...kept, ...incoming.filter((f) => !seen.has(filterKey(f)))];
}

/** A rule in words. A badge saying `replace` tells nobody what it does. */
function describe(t: {
  col: string;
  kind: string;
  find: string;
  replace: string;
  sources?: string[];
}): string {
  // A rule with sources produces the column rather than changing it, so it
  // reads the other way round: "X from Y", not "X: do this".
  if (t.sources && t.sources.length > 0) {
    const from = t.sources.join(', ');
    switch (t.kind) {
      case 'combine': return `${t.col} = ${from} joined by "${t.find}"`;
      case 'domain': return `${t.col} = domain of ${from}`;
      case 'phone_uk': return `${t.col} = ${from} tidied as a UK number`;
      case 'part': return `${t.col} = piece ${t.replace} of ${from} split on "${t.find}"`;
      case 'before': return `${t.col} = ${from} before "${t.find}"`;
      case 'after': return `${t.col} = ${from} after "${t.find}"`;
      case 'left': return `${t.col} = first ${t.replace} characters of ${from}`;
      case 'right': return `${t.col} = last ${t.replace} characters of ${from}`;
      default: return `${t.col} from ${from}`;
    }
  }
  switch (t.kind) {
    case 'trim': return `${t.col}: trim spaces`;
    case 'upper': return `${t.col}: UPPERCASE`;
    case 'lower': return `${t.col}: lowercase`;
    case 'clear': return `${t.col}: cleared`;
    case 'fill_empty': return `${t.col}: empty cells set to "${t.replace}"`;
    case 'replace':
      return t.replace === ''
        ? `${t.col}: remove "${t.find}"`
        : `${t.col}: "${t.find}" becomes "${t.replace}"`;
    default: return `${t.col}: ${t.kind}`;
  }
}

/**
 * How long to sit on an edit before saving it.
 *
 * Typing must never wait on the network, so a change lands in local state
 * immediately and the flush is debounced behind it. Two seconds is long enough
 * that editing several cells in a row becomes one request, and short enough
 * that nobody navigates away wondering whether it saved.
 */
const FLUSH_AFTER_MS = 2000;

/** A cell being typed into, addressed the way the server addresses it. */
type Editing = { rowNo: number; col: string; value: string; initial?: string };

/** One step of history: what was done, and what every cell held before it. */
type UndoOp = { label: string; changes: Change[] };

/** How many steps back are kept. See the note where the stack is declared. */
const UNDO_DEPTH = 100;

export function SheetViewPage() {
  const words = useVocab();
  const { id = null } = useParams();
  const [search] = useSearchParams();
  /* Reading an older version. Read-only: the overlay and the bulk rules are
     changes to the current version, so there is nothing coherent to edit. */
  const viewingVersion = Number(search.get('version')) || null;
  /* Where the grid is, reported by it. Nothing here decides it any more. */
  const [visible, setVisible] = useState({ first: 1, last: 1 });
  /* A row to jump to, consumed by the grid and then irrelevant. */
  /**
   * Where to jump, and how many times we have been asked.
   *
   * The counter is not decoration. The grid scrolls when this changes, and a
   * bare row number does not change when you ask for the same row twice — so
   * scrolling away and asking again for the row you just left did nothing at
   * all. Carrying a counter makes every request distinct.
   */
  const [jumpTo, setJumpTo] = useState<{ row: number; nth: number } | null>(null);
  const [jumpText, setJumpText] = useState('');

  /** Back to the first row, as a fresh request. */
  function toTop() {
    setJumpTo((prev) => ({ row: 1, nth: (prev?.nth ?? 0) + 1 }));
  }

  function jump() {
    const n = Number(jumpText);
    if (!n) return;
    setJumpTo((prev) => ({
      row: Math.max(1, Math.min(n, total)),
      nth: (prev?.nth ?? 0) + 1,
    }));
  }
  /* How the file is being looked at. Not part of the file and not saved: a
     sort is a way of reading, not a change to the data. */
  const [view, setView] = useState<SheetView>({ filters: [] });
  // A different file starts unfiltered. The page is reused when moving from a
  // file to one made from it, and the filters that made it would otherwise
  // carry over and read as "40 of 40", filtering a file that is already them.
  useEffect(() => {
    setView({ filters: [] });
  }, [id]);
  /* Where the last right-click landed, and what it landed on. Null when
     closed, which is also what dismisses the menu. */
  const [cellMenu, setCellMenu] = useState<CellMenuTarget | null>(null);
  /* Pasting and row-number arithmetic only hold in the file's own order. */
  const isNatural = !view.sort && view.filters.length === 0;
  /* Cells the user has picked out, to ask how common their values are.
     Keyed by row and column so the same value in two rows is one question. */
  const [selected, setSelected] = useState<{ rowNo: number; col: string; value: string }[]>([]);
  /*
    Bumped to drop the selection. The grid holds it, because it is the only
    thing that knows what a cell says, so clearing has to be asked for rather
    than done.
  */
  const [clearSelection, setClearSelection] = useState(0);
  /* Filled in by the grid; see the note on `cellLookup` there. */
  const cellLookup = useRef<((row: number, col: number) => GridCell | null) | null>(null);
  /*
    Counting is a scan of the file, so it waits for the selection to settle.
    Firing on every click made picking through cells feel heavy: each one
    started a query that the next click made irrelevant.
  */
  const [settledSelection] = useDebouncedValue(selected, 450);
  const countCells: SheetCell[] = useMemo(
    () =>
      Object.values(
        Object.fromEntries(
          settledSelection.map((x) => [`${x.col}\u0000${x.value}`, { col: x.col, value: x.value }]),
        ),
      ),
    [settledSelection],
  );

  /*
    Applying a filter, wherever it was asked for.

    Replaces any existing filter on the same column rather than stacking a
    second one: two conditions on one column read as a contradiction in the
    chip row, and the last thing clicked is what was meant.
  */
  function applyFilters(filters: SheetFilter[]) {
    toTop();
    setSelected([]);
    setView((v) => ({ ...v, filters: mergeFilters(v.filters, filters) }));
  }

  const sheet = useSheet(id);
  const ready = sheet.data?.status === 'ready';
  /*
    One small read alongside the grid's own blocks: the first rows, for the
    edited-cell flags and the filtered count. The grid fetches what it shows;
    this answers what the page around it needs to say.
  */
  const page = useSheetRows(id, 0, 1, ready, viewingVersion, view);
  const save = useSaveSheetEdits(id);
  const discard = useDiscardSheetEdits(id);
  const commit = useCommitSheet(id);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  /* Which way the rows are being cut: null when the dialog is closed. */
  const [cutting, setCutting] = useState<'remove' | 'keep' | null>(null);
  /* Which column the worked-out-column dialog was opened from, or null. */
  const [computingFrom, setComputingFrom] = useState<string | null>(null);
  const [formulaFrom, setFormulaFrom] = useState<string | null>(null);
  const [splitting, setSplitting] = useState(false);
  const [suppressing, setSuppressing] = useState(false);
  /* Undoing throws away work that was never saved into a version, so there is
     nothing to restore it from. It sits beside "Apply to file" and appears the
     moment anything is typed, which is exactly the arrangement that gets it
     clicked by mistake. */
  const [confirmingUndo, setConfirmingUndo] = useState(false);
  const reorder = useReorderSheetColumns(id);
  const deleteRows = useDeleteSheetRows(id);
  const openNewFile = useOpenNewFile();
  const [savingNew, setSavingNew] = useState(false);
  const saveDetails = useNewFileDetails(
    savingNew,
    `${fileStem(sheet.data?.file_name ?? 'file')} - edited.csv`,
    `${sheet.data?.file_name ?? 'The original'} with edits saved.`,
  );
  async function saveAsNewFile() {
    /*
      Anything still buffered has to reach the server first, or the new file
      would be written without the last thing typed. Waiting is the point.
    */
    try {
      await flush();
    } catch {
      // The save reported itself; writing now would miss those cells.
      return;
    }
    commit.mutate(
      {
        baseVersion: sheet.data?.version ?? 1,
        name: saveDetails.name,
        description: saveDetails.description,
      },
      {
        onSuccess: (made) => {
          setSavingNew(false);
          openNewFile(made, sheet.data?.file_name ?? 'The original');
        },
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Could not save',
            message: (e as Error).message,
          }),
      },
    );
  }
  /*
    Suggested name and description for the file Keep or Remove makes, built
    from the filters so the Files list says what each file is without being
    opened: "Walsall leads.csv", "Rows where town contains Walsall".
  */
  const filterWords = view.filters.map(describeFilter).join(', ');
  // For the name, just the values: "Walsall, tiktok_ads" reads as a file name
  // where 'town contains "Walsall"' does not.
  const filterValues = view.filters.map((f) => f.value || f.col).join(', ');
  const cutDetails = useNewFileDetails(
    cutting !== null,
    `${fileStem(sheet.data?.file_name ?? 'file')} - ${cutting === 'keep' ? '' : 'without '}${filterValues}.csv`,
    cutting === 'keep'
      ? `Rows from ${sheet.data?.file_name ?? 'the original'} where ${filterWords}.`
      : `${sheet.data?.file_name ?? 'The original'} without the rows where ${filterWords}.`,
  );
  /* Totals are a scan of every column, so they are asked for, not shown. */
  const [showTotals, setShowTotals] = useState(false);
  const stats = useSheetStats(id, view.filters, showTotals && ready);
  const transforms = useSheetTransforms(id);
  const dropTransform = useDeleteSheetTransform(id);

  const [editing, setEditing] = useState<Editing | null>(null);
  /*
    Cells changed but not yet sent. Keyed `rowNo\u0000col` so a cell edited
    twice before a flush is one entry, and so the buffer survives paging — you
    can change a cell, scroll away, and it still saves.
  */
  const [pending, setPending] = useState<
    Map<string, { row_no: number; col: string; val: string; revert?: boolean }>
  >(new Map());
  /*
    What each change did, so one of them can be taken back.

    A stack of operations rather than of cells: typing, clearing, filling down
    and pasting each touch a different number of cells, and Cmd+Z means "that
    thing I just did", not "one of the cells it happened to touch".

    Bounded, because it holds the old values and a paste can be five thousand
    of them. Losing the oldest step of a long session is a fair trade for not
    growing without limit.
  */
  const [undoStack, setUndoStack] = useState<UndoOp[]>([]);
  const [redoStack, setRedoStack] = useState<UndoOp[]>([]);
  const timer = useRef<number | null>(null);
  /*
    The buffer as the flush timer will find it.

    The flush runs from a timeout two seconds after the last keystroke, so it
    cannot read `pending` from the closure it was created in. Reading it inside
    a state updater worked, but made saving a side effect of setting state,
    which React is free to run twice.
  */
  const pendingRef = useRef(pending);
  useEffect(() => { pendingRef.current = pending; }, [pending]);

  /**
   * Send what has been typed, and keep showing it until the file agrees.
   *
   * The buffer is what puts an edited value on screen; the grid falls back to
   * the server's rows for any cell not in it. So dropping it the moment the
   * request goes out uncovers the old value, and the edit appears to undo
   * itself until a refetch lands - which is exactly what it did, for as long
   * as the refetch was of a query the grid does not read.
   *
   * Clearing only the keys that were sent matters: a cell typed into while the
   * save was in flight must survive it.
   */
  const flush = async () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    const buffered = [...pendingRef.current.values()];
    if (buffered.length === 0) return;
    // `null` is how the endpoint is told the cell has no edit of its own any
    // more, which is what undoing back past the first change to it means. An
    // empty string would be an edit that happens to be blank.
    const batch = buffered.map((e) => ({
      row_no: e.row_no,
      col: e.col,
      val: e.revert ? null : e.val,
    }));
    await save.mutateAsync(batch, {
      // Runs after the refetches the mutation triggers have settled, so the
      // grid already holds the saved values by the time the overlay goes.
      onSuccess: () =>
        setPending((p) => {
          const next = new Map(p);
          for (const e of buffered) {
            const key = `${e.row_no}\u0000${e.col}`;
            // Not if it has been typed into again since it was sent.
            if (next.get(key)?.val === e.val) next.delete(key);
          }
          return next;
        }),
      onError: (e) =>
        notifications.show({
          color: 'red',
          title: 'Could not save those changes',
          // The buffer is deliberately not cleared on failure, so the next
          // flush retries rather than losing what was typed.
          message: (e as Error).message,
        }),
    });
  };

  /** Flush without caring when it lands, for the debounce and plain buttons. */
  const flushSoon = () => { void flush().catch(() => {}); };

  // Never leave typed-but-unsent edits behind when the component goes away.
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);




  /**
   * Put values into cells, however they were arrived at.
   *
   * Typing, pasting, clearing and filling down are all the same thing to
   * everything downstream: a batch of cells with new values, buffered on
   * screen and flushed together. Written once so they cannot drift.
   */
  function writeCells(
    cells: { rowNo: number; col: string; val: string; revert?: boolean }[],
  ) {
    if (cells.length === 0 || !editable) return;
    setPending((p) => {
      const next = new Map(p);
      for (const e of cells) {
        next.set(`${e.rowNo}\u0000${e.col}`, {
          row_no: e.rowNo,
          col: e.col,
          val: e.val,
          revert: e.revert,
        });
      }
      return next;
    });
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flushSoon, FLUSH_AFTER_MS);
  }

  /**
   * Make a change and remember how to take it back.
   *
   * Every route into the file that writes cells comes through here — typing,
   * pasting, clearing, filling down — so there is one place that knows what
   * happened and one history rather than four.
   */
  function apply(changes: Change[], label: string) {
    // Typing a value a cell already held is not a change, and should not take
    // up a step of history that appears to do nothing when it is undone.
    const real = changes.filter((c) => c.after !== c.before);
    if (real.length === 0 || !editable) return;
    writeCells(real.map((c) => ({ rowNo: c.rowNo, col: c.col, val: c.after })));
    setUndoStack((st) => [...st, { label, changes: real }].slice(-UNDO_DEPTH));
    // Doing something new is what ends a line of redos, the same as everywhere
    // else: the future those steps belonged to no longer exists.
    setRedoStack([]);
  }

  /** Put a set of cells back to the values a change found them at. */
  function revert(changes: Change[]) {
    writeCells(
      changes.map((c) => ({
        rowNo: c.rowNo,
        col: c.col,
        // Shown straight away, because the value a cell had before it was
        // first edited is exactly what the file says. Waiting for the server
        // would blink the new value for as long as the round trip takes.
        val: c.before,
        // And with no edit of its own left behind, if there was none before.
        revert: !c.beforeEdited,
      })),
    );
  }

  function undo() {
    const op = undoStack[undoStack.length - 1];
    if (!op) return;
    revert(op.changes);
    setUndoStack((st) => st.slice(0, -1));
    setRedoStack((st) => [...st, op].slice(-UNDO_DEPTH));
    notifications.show({
      color: 'gray',
      title: `Undone: ${op.label}`,
      message: `${formatNumber(op.changes.length)} ${op.changes.length === 1 ? 'cell' : 'cells'}`,
      autoClose: 1500,
    });
  }

  function redo() {
    const op = redoStack[redoStack.length - 1];
    if (!op) return;
    writeCells(op.changes.map((c) => ({ rowNo: c.rowNo, col: c.col, val: c.after })));
    setRedoStack((st) => st.slice(0, -1));
    setUndoStack((st) => [...st, op].slice(-UNDO_DEPTH));
  }

  /**
   * Copy the top row of the selection down over the rest of it.
   *
   * The spreadsheet gesture, and the one people reach for after typing a value
   * they want repeated. A selection one row tall has nothing below it to fill,
   * so it does nothing rather than guessing how far down to go.
   */
  function fillDown(cells: GridCell[]) {
    if (cells.length === 0) return;
    const top = Math.min(...cells.map((c) => c.rowNo));
    const source = new Map(cells.filter((c) => c.rowNo === top).map((c) => [c.col, c.value]));
    const changes = cells
      .filter((c) => c.rowNo !== top && source.has(c.col))
      .map((c) => ({
        rowNo: c.rowNo,
        col: c.col,
        before: c.value,
        beforeEdited: c.edited,
        after: source.get(c.col) ?? '',
      }));
    if (changes.length === 0) return;
    apply(changes, 'fill down');
    notifications.show({
      color: 'teal',
      title: 'Filled down',
      message: `${formatNumber(changes.length)} cells. Saving shortly.`,
    });
  }

  /**
   * Paste a block of cells in from a spreadsheet.
   *
   * Clipboard data from Excel, Sheets or Numbers is tab-separated rows, so it
   * lands as a rectangle anchored at the selected cell. It goes through the
   * same batched save as typing — a paste is many edits, and the endpoint was
   * built to take many at once.
   *
   * Only in the file's own order. Row numbers are dense, so the row below the
   * anchor is anchor + 1 and a paste can run past the visible page; under a
   * sort or filter that is not true, and the rows written would be whichever
   * ones happened to be adjacent on screen.
   */
  function pasteAt(anchor: { rowNo: number; col: string }, text: string) {
    if (!isNatural) {
      notifications.show({
        color: 'yellow',
        title: 'Clear the sort and filters to paste',
        message: 'Pasting writes to rows by their position in the file.',
      });
      return;
    }
    const grid = text.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n').map((r) => r.split('\t'));
    const startCol = columns.indexOf(anchor.col);
    if (startCol < 0) return;

    /*
      What each target cell holds now, so the paste can be taken back.

      Pasting only runs in the file's own order, so the row on screen at index
      N is row N + 1, and the grid can be asked about it directly.

      A row nobody has scrolled to has not been fetched, and there is no honest
      answer for what it holds. Those cells are pasted into like any other and
      left out of the history, rather than guessed at: undoing to a wrong old
      value would be worse than undoing less than everything, and the paste
      says so at the time.
    */
    const changes: Change[] = [];
    let unknown = 0;
    let count = 0;
    grid.forEach((cells, r) => {
      cells.forEach((val, cIdx) => {
        const colIdx = startCol + cIdx;
        const col = columns[colIdx];
        // Past the last column is dropped rather than wrapped: a spreadsheet
        // does the same, and wrapping would scatter values across rows.
        if (!col) return;
        const rowNo = anchor.rowNo + r;
        if (rowNo > fileRows) return;
        count += 1;
        const was = cellLookup.current?.(rowNo - 1, colIdx) ?? null;
        if (was) changes.push({ rowNo, col, before: was.value, beforeEdited: was.edited, after: val });
        else unknown += 1;
      });
    });
    if (count === 0) return;
    if (count > 5000) {
      notifications.show({
        color: 'red',
        title: 'That paste is too big',
        message: `${formatNumber(count)} cells at once. Paste up to 5,000.`,
      });
      return;
    }

    // Written from the clipboard, which is the whole rectangle; only the
    // history is limited to the part that can be put back.
    writeCells(
      grid.flatMap((cells, r) =>
        cells.flatMap((val, cIdx) => {
          const col = columns[startCol + cIdx];
          const rowNo = anchor.rowNo + r;
          if (!col || rowNo > fileRows) return [];
          return [{ rowNo, col, val }];
        }),
      ),
    );
    if (changes.length > 0) {
      setUndoStack((st) => [...st, { label: 'paste', changes }].slice(-UNDO_DEPTH));
      setRedoStack([]);
    }
    notifications.show({
      color: 'teal',
      title: 'Pasted',
      message:
        unknown > 0
          ? `${formatNumber(count)} cells. Undo covers all but ${formatNumber(unknown)} of them, on rows that were not on screen.`
          : `${formatNumber(count)} cells. Saving shortly.`,
    });
  }

  // What the pager counts through: the matches when filtered, the file
  // otherwise. Counting the whole file while showing nine rows would put the
  // pager wildly out of step with the grid.
  const total = page.data?.matching_rows ?? sheet.data?.row_count ?? 0;
  const fileRows = sheet.data?.row_count ?? 0;
  const columns = sheet.data?.columns ?? [];
  const editable = (sheet.data?.editable ?? false) && (page.data?.is_current ?? true);
  /* Cells the server says differ from the file, as `row\u0000col` keys. */
  /* A bulk rule is an unsaved change too, so it must offer the same actions. */
  /*
    Counted across the sheet, not across the rows on screen.

    `edited` is scoped to the rows the page returned, and this page asks for
    one row so that the grid can fetch its own. Deciding from it whether there
    is anything to save meant an edit to row five thousand left no way to save
    or discard it: both buttons are behind this.
  */
  const editedCells = (page.data?.edited_cells ?? 0) + pending.size;
  const ruleCount = (transforms.data?.transforms ?? []).length;
  const hasChanges =
    pending.size > 0 ||
    (page.data?.edited_cells ?? 0) > 0 ||
    (transforms.data?.transforms ?? []).length > 0;

  /*
    Paste goes to whatever has focus, and a table cell is not focusable, so
    this listens on the document — but only while a cell is picked out, so it
    cannot swallow a paste meant for the go-to-row box or a cell being typed
    into.
  */
  useEffect(() => {
    const anchor = selected[selected.length - 1];
    if (!anchor || !editable || editing) return;
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      const text = e.clipboardData?.getData('text/plain');
      if (!text) return;
      e.preventDefault();
      pasteAt(anchor, text);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [selected, editable, editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const failure = sheet.data?.error_message ?? page.error?.message ?? sheet.error?.message;

  return (
    <Stack gap="xs" h="calc(100vh - 3rem)">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 260px' }}>
          <Tooltip label="Back to files">
            <ActionIcon component={Link} to="/sheets" variant="subtle" color="gray" aria-label="Back to files">
              <IconArrowLeft size={17} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={sheet.data?.file_name ?? ''} disabled={!sheet.data} openDelay={400}>
            <Text fw={600} truncate>{sheet.data?.file_name ?? 'Loading…'}</Text>
          </Tooltip>
          {ready && (
            <Badge variant="light" color="gray" style={{ flexShrink: 0 }}>
              {view.filters.length > 0
                ? `${formatNumber(total)} of ${formatNumber(fileRows)} rows`
                : `${formatNumber(total)} rows`}{' '}
              · {columns.length} columns
            </Badge>
          )}
        </Group>
        <Group gap="xs" wrap="nowrap">
          {pending.size > 0 && (
            <Text size="xs" c="dimmed">
              {pending.size} unsaved
            </Text>
          )}
          {(pending.size > 0 || save.isPending) && (
            <Button
              size="compact-sm"
              variant="light"
              loading={save.isPending}
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={flushSoon}
            >
              Save now
            </Button>
          )}
          {hasChanges && (
            <>
              <Tooltip label="Save a new file with these changes written in. This file is left as it is.">
                <Button
                  size="compact-sm"
                  loading={commit.isPending}
                  onClick={() => setSavingNew(true)}
                >
                  Save as new file
                </Button>
              </Tooltip>
              {/* Step at a time first, so the all-or-nothing one is not what
                  gets reached for by habit. */}
              <Tooltip
                label={
                  undoStack.length > 0
                    ? `Undo ${undoStack[undoStack.length - 1]!.label}`
                    : 'Nothing to undo'
                }
              >
                {/* Wrapped, because a disabled button fires no events, and a
                    tooltip that never appears cannot say why it is disabled. */}
                <span>
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="gray"
                    disabled={undoStack.length === 0}
                    leftSection={<IconArrowBackUp size={14} />}
                    onClick={undo}
                  >
                    Undo
                  </Button>
                </span>
              </Tooltip>
              {redoStack.length > 0 && (
                <Tooltip label={`Redo ${redoStack[redoStack.length - 1]!.label}`}>
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconArrowForwardUp size={14} />}
                    onClick={redo}
                  >
                    Redo
                  </Button>
                </Tooltip>
              )}
              <Tooltip label="Put every cell back to what the file says">
                <Button
                  size="compact-sm"
                  variant="subtle"
                  color="gray"
                  loading={discard.isPending}
                  leftSection={<IconPencilOff size={14} />}
                  onClick={() => setConfirmingUndo(true)}
                >
                  Discard all changes
                </Button>
              </Tooltip>
            </>
          )}
          {ready && (
            <Tooltip label="Totals and ranges for the columns that hold numbers">
              <Button
                size="compact-sm"
                variant={showTotals ? 'light' : 'default'}
                leftSection={<IconSum size={14} />}
                onClick={() => setShowTotals((v) => !v)}
              >
                Totals
              </Button>
            </Tooltip>
          )}
          {/*
            Both directions, because filtering is used both ways: to find the
            rows to get rid of, and to whittle a file down to the ones worth
            keeping. Only "remove" existed, so anyone who had filtered to what
            they wanted had to invert every filter by hand to save it — and the
            opposite of "not empty and not no" is not two more filters.
          */}
          {ready && view.filters.length > 0 && (
            <Tooltip label="Keep only the rows currently shown, as a new version">
              <Button
                size="compact-sm"
                variant="default"
                loading={deleteRows.isPending}
                leftSection={<IconFilter size={14} />}
                onClick={() => setCutting('keep')}
              >
                Keep {formatNumber(total)} rows
              </Button>
            </Tooltip>
          )}
          {ready && view.filters.length > 0 && (
            <Tooltip label="Remove the rows currently shown, as a new version">
              <Button
                size="compact-sm"
                variant="default"
                color="red"
                loading={deleteRows.isPending}
                leftSection={<IconTrash size={14} />}
                onClick={() => setCutting('remove')}
              >
                Remove {formatNumber(total)} rows
              </Button>
            </Tooltip>
          )}
          {ready && (
            <Tooltip label={`Send these rows into ${words.a('campaign')} as ${words.many('lead')}`}>
              <Button
                size="compact-sm"
                variant="default"
                leftSection={<IconDatabaseImport size={14} />}
                onClick={() => { flushSoon(); setImporting(true); }}
              >
                Import
              </Button>
            </Tooltip>
          )}
          {/* Labelled for both directions it now does. It was "Suppress",
              which described the removing half and hid the keeping half:
              somebody looking for the eligibility check had no reason to press
              a button that said it would take rows away. */}
          {ready && editable && (
            <Tooltip label="Keep or remove the rows that appear in another file">
              <Button
                size="compact-sm"
                variant="default"
                leftSection={<IconFilterOff size={14} />}
                onClick={() => setSuppressing(true)}
              >
                Compare
              </Button>
            </Tooltip>
          )}
          {ready && (
            <Tooltip label={`Download in blocks, one per ${words.one('buyer')}`}>
              <Button
                size="compact-sm"
                variant="default"
                leftSection={<IconScissors size={14} />}
                onClick={() => setSplitting(true)}
              >
                Split
              </Button>
            </Tooltip>
          )}
          {ready && (
            <Tooltip label="Download as CSV, with every change applied">
              <Button
                size="compact-sm"
                variant="default"
                loading={exporting}
                leftSection={<IconDownload size={14} />}
                onClick={async () => {
                  flush();
                  setExporting(true);
                  try {
                    await downloadSheet(
                      id!,
                      sheet.data?.file_name ?? 'sheet',
                      undefined,
                      undefined,
                      view.filters,
                    );
                  } catch (e) {
                    notifications.show({
                      color: 'red',
                      title: 'Download failed',
                      message: (e as Error).message,
                    });
                  }
                  setExporting(false);
                }}
              >
                Export
              </Button>
            </Tooltip>
          )}
          {page.isFetching && <Loader size="xs" />}
        </Group>
      </Group>
      {sheet.data && <FileDetails sheet={sheet.data} />}

      {page.data && !page.data.is_current && (
        <Alert color="chartAmber" variant="light">
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm">
              Showing version {page.data.version} as it was saved. It cannot be changed,
              because editing applies to the current version.
            </Text>
            <Button
              size="compact-xs"
              variant="default"
              component={Link}
              to={`/sheets/${id}`}
            >
              Back to current
            </Button>
          </Group>
        </Alert>
      )}

      {(view.sort || view.filters.length > 0) && (
        <Group gap={6} wrap="wrap">
          <Text size="xs" c="dimmed">Showing:</Text>
          {view.sort && (
            <Badge
              size="sm"
              variant="light"
              color="gray"
              style={{ textTransform: 'none' }}
              rightSection={
                <ActionIcon
                  size={14}
                  variant="transparent"
                  color="gray"
                  aria-label="Stop sorting"
                  onClick={() => { toTop(); setView((v) => ({ ...v, sort: null })); }}
                >
                  <IconX size={11} />
                </ActionIcon>
              }
            >
              sorted by {view.sort} {view.dir === 'desc' ? 'Z–A' : 'A–Z'}
            </Badge>
          )}
          {view.filters.map((f) => (
            <Badge
              key={filterKey(f)}
              size="sm"
              variant="light"
              color="blue"
              style={{ textTransform: 'none' }}
              rightSection={
                <ActionIcon
                  size={14}
                  variant="transparent"
                  color="blue"
                  aria-label={`Remove the filter: ${describeFilter(f)}`}
                  onClick={() => {
                    toTop();
                    // This filter, not every filter on its column: a column can
                    // now carry several exclusions and removing one must leave
                    // the others alone.
                    setView((v) => ({
                      ...v,
                      filters: v.filters.filter((x) => filterKey(x) !== filterKey(f)),
                    }));
                  }}
                >
                  <IconX size={11} />
                </ActionIcon>
              }
            >
              {describeFilter(f)}
            </Badge>
          ))}
        </Group>
      )}

      {(transforms.data?.transforms ?? []).length > 0 && (
        <Group gap={6} wrap="wrap">
          <Text size="xs" c="dimmed">Applied to every row:</Text>
          {(transforms.data?.transforms ?? []).map((t) => (
            <Badge
              key={t.id}
              size="sm"
              variant="light"
              color="chartAmber"
              style={{ textTransform: 'none' }}
              rightSection={
                <ActionIcon
                  size={14}
                  variant="transparent"
                  color="chartAmber"
                  aria-label={`Remove ${describe(t)}`}
                  onClick={() => dropTransform.mutate(t.id)}
                >
                  <IconX size={11} />
                </ActionIcon>
              }
            >
              {describe(t)}
            </Badge>
          ))}
        </Group>
      )}

      {ready && !editable && (
        <Alert color="gray" variant="light">
          <Text size="sm">
            This file was opened before cells could be edited. It is being prepared again
            in the background; reload in a moment to edit it.
          </Text>
        </Alert>
      )}

      {failure && (
        <Alert color="red" variant="light" title="This file cannot be viewed">
          <Text size="sm">{failure}</Text>
        </Alert>
      )}

      {/* Loading and preparing are different things, and saying the wrong one
          claims work is happening that is not. A file that is merely being
          fetched should not announce a conversion. */}
      {!failure && sheet.isPending && (
        <Group gap="sm" py="xl" justify="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading…</Text>
        </Group>
      )}

      {!failure && !sheet.isPending && !ready && (
        <Group gap="sm" py="xl" justify="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Preparing the file. This happens once; opening it again is instant.
          </Text>
        </Group>
      )}

      <Modal
        opened={confirmingUndo}
        onClose={() => setConfirmingUndo(false)}
        title="Discard all changes"
        size="md"
      >
        <Stack>
          <Alert color="red" variant="light">
            <Text size="sm">
              Every change since the last save goes: {editedCells > 0 && (
                <>{formatNumber(editedCells)} edited cell{editedCells === 1 ? '' : 's'}</>
              )}
              {editedCells > 0 && ruleCount > 0 && ' and '}
              {ruleCount > 0 && (
                <>{formatNumber(ruleCount)} column rule{ruleCount === 1 ? '' : 's'}</>
              )}
              {editedCells === 0 && ruleCount === 0 && 'everything typed since the last save'}.
            </Text>
            <Text size="sm" mt={6}>
              These were never written into a version, so there is nothing to bring them back
              from. The file itself is untouched.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirmingUndo(false)}>
              Keep them
            </Button>
            <Button
              color="red"
              loading={discard.isPending}
              onClick={() => {
                // The buffer holds changes too, and a flush already in the
                // diary would put back what is about to be thrown away.
                if (timer.current) {
                  window.clearTimeout(timer.current);
                  timer.current = null;
                }
                setPending(new Map());
                discard.mutate();
                setConfirmingUndo(false);
              }}
            >
              Undo them
            </Button>
          </Group>
        </Stack>
      </Modal>

      {ready && !failure && (
        <>
          <Box style={{ flex: 1, minHeight: 0 }}>
            <SheetGrid
              sheetId={id}
              columns={columns}
              totalRows={total}
              ready={ready}
              version={viewingVersion}
              view={view}
              editable={editable}
              height="100%"
              editing={editing}
              pending={pending}
              scrollTo={jumpTo}
              clearSelection={clearSelection}
              cellLookup={cellLookup}
              onSelectionChange={setSelected}
              onCellContextMenu={({ x, y, col, value, inSelection }) => {
                /*
                  The selection the menu should act on.

                  Taken from the live selection rather than the debounced one
                  the counting bar uses: the click that opened this menu may
                  have just changed it, and a menu offering to filter to the
                  previous selection would be worse than offering nothing.
                */
                const distinct = inSelection
                  ? Object.values(
                      Object.fromEntries(
                        selected.map((c) => [
                          `${c.col}\u0000${c.value}`,
                          { col: c.col, value: c.value },
                        ]),
                      ),
                    )
                  : [{ col, value }];
                setCellMenu({ x, y, col, value, selection: distinct });
              }}
              onClearCells={(changes) => apply(changes, 'clear')}
              onFillDown={fillDown}
              onUndo={undo}
              onRedo={redo}
              onCopyProblem={(why) =>
                notifications.show({ color: 'yellow', title: 'Nothing copied', message: why })
              }
              onStartEdit={(cell) => setEditing(cell)}
              onCommitEdit={(change) => {
                apply([change], 'edit');
                setEditing(null);
              }}
              onCancelEdit={() => setEditing(null)}
              onVisibleRowsChange={(first, last) => setVisible({ first, last })}
              renderHeaderCell={(c) => (
                <>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.4px',
                      textTransform: 'uppercase',
                      color: 'var(--mantine-color-dimmed)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c}
                  </span>
                  {view.sort === c && (
                    <Text span size="xs" c="dimmed">{view.dir === 'desc' ? '↓' : '↑'}</Text>
                  )}
                  <SheetColumnMenu
                    sheetId={id}
                    fileName={sheet.data?.file_name ?? 'file'}
                    column={c}
                    columns={columns}
                    disabled={!editable}
                    baseVersion={sheet.data?.version ?? 1}
                    canMoveLeft={columns.indexOf(c) > 0}
                    canMoveRight={columns.indexOf(c) < columns.length - 1}
                    onCompute={() => setComputingFrom(c)}
                    onFormula={() => setFormulaFrom(c)}
                    onMove={(by) => {
                      const from = columns.indexOf(c);
                      const next = [...columns];
                      next.splice(from, 1);
                      next.splice(from + by, 0, c);
                      reorder.mutate(next, {
                        onError: (e) =>
                          notifications.show({
                            color: 'red',
                            title: 'Could not move that column',
                            message: (e as Error).message,
                          }),
                      });
                    }}
                    onSort={(dir) => setView((v) => ({ ...v, sort: c, dir }))}
                    onFilter={(f) =>
                      setView((v) => ({
                        ...v,
                        filters: [...v.filters.filter((x) => x.col !== f.col), f],
                      }))
                    }
                  />
                </>
              )}
            />
          </Box>

          {showTotals && (
            <SheetTotalsBar
              columns={stats.data?.columns ?? []}
              rows={stats.data?.rows ?? 0}
              loading={stats.isPending}
              error={stats.error?.message}
            />
          )}

          <SheetFormulaBar
            sheetId={id}
            columns={columns}
            column={selected.length > 0 ? (selected[selected.length - 1]?.col ?? null) : null}
            transforms={transforms.data?.transforms ?? []}
            baseVersion={sheet.data?.version ?? 1}
            editable={editable}
          />

          <SheetCellMenu
            target={cellMenu}
            hasFilters={view.filters.length > 0}
            onClose={() => setCellMenu(null)}
            onFilter={applyFilters}
            onSort={(col, dir) => {
              toTop();
              setView((v) => ({ ...v, sort: col, dir }));
            }}
            onClearFilters={() => {
              toTop();
              setView((v) => ({ ...v, filters: [] }));
            }}
            onCopy={(value) => {
              navigator.clipboard.writeText(value).catch(() => {
                notifications.show({
                  color: 'yellow',
                  title: 'Nothing copied',
                  message: 'The browser would not give us the clipboard',
                });
              });
            }}
          />

          <SheetSelectionBar
            sheetId={id}
            /* Deduplicated: the same value in the same column twice is one
               question, and each cell costs a condition on the server. */
            cells={countCells}
            /* Shown immediately from the live selection; counted from the
               settled one, so the bar appears on the first click and the
               number arrives when the clicking stops. */
            selectedCount={selected.length}
            onClear={() => setClearSelection((n) => n + 1)}
            onFilter={applyFilters}
          />

          <Group justify="space-between" pb={4}>
            <Text size="sm" c="dimmed">
              {/* What is on screen, reported by the grid — the page no longer
                  has a page to speak of. */}
              {formatNumber(visible.first)}–{formatNumber(Math.min(visible.last, total))} of{' '}
              {formatNumber(total)}
              {view.filters.length > 0 && ` (filtered from ${formatNumber(fileRows)})`}
            </Text>

            <Group gap="xs">
              {/*
                Filtered, this counts matches rather than rows of the file, and
                saying "row" made that look broken: typing 30,000,000 into a
                filtered view lands on the 30 millionth match, whose own number
                in the file is 40,002,043, and that is the number the # column
                shows. Both are right; only the word was wrong.
              */}
              <Text size="sm" c="dimmed">
                {view.filters.length > 0 ? 'Go to match' : 'Go to row'}
              </Text>
              {/* A plain input with a button, not a NumberInput: that
                  component handles keys itself and never passed Enter on, so
                  typing a row number and pressing return did nothing. */}
              <Tooltip
                withArrow
                multiline
                w={260}
                disabled={view.filters.length === 0}
                label={
                  `Counts the ${formatNumber(total)} rows your filters match. ` +
                  'The # column keeps each row\'s own number in the file, so it reads higher.'
                }
              >
                <TextInput
                  size="xs"
                  w={130}
                  inputMode="numeric"
                  placeholder={view.filters.length > 0 ? 'e.g. 500000th match' : 'e.g. 500000'}
                  value={jumpText}
                  onChange={(e) => setJumpText(e.currentTarget.value.replace(/[^0-9]/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') jump();
                  }}
                />
              </Tooltip>
              <Button size="compact-xs" variant="default" disabled={!jumpText} onClick={jump}>
                Go
              </Button>
            </Group>
          </Group>
        </>
      )}

      <Modal
        opened={savingNew}
        onClose={() => setSavingNew(false)}
        title="Save your changes as a new file"
        size="sm"
      >
        <Stack>
          <NewFileFields details={saveDetails} sourceName={sheet.data?.file_name ?? 'This file'} />
          <Text size="xs" c="dimmed">
            Your unsaved changes stay on this file too, until you discard them here.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setSavingNew(false)}>Cancel</Button>
            <Button loading={commit.isPending} disabled={!saveDetails.name.trim()} onClick={saveAsNewFile}>
              Save as new file
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={cutting !== null}
        onClose={() => setCutting(null)}
        title={cutting === 'keep' ? 'Save these rows as a new file' : 'Save the rest as a new file'}
        size="sm"
      >
        <Stack>
          <Text size="sm">
            {cutting === 'keep' ? (
              <>
                The new file will hold the {formatNumber(total)} of {formatNumber(fileRows)} rows
                that match the current filters.
              </>
            ) : (
              <>
                The new file will hold the {formatNumber(Math.max(0, fileRows - total))} rows that
                do <b>not</b> match the current filters, leaving out these {formatNumber(total)}.
              </>
            )}
          </Text>
          <NewFileFields details={cutDetails} sourceName={sheet.data?.file_name ?? 'This file'} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCutting(null)}>Cancel</Button>
            <Button
              color={cutting === 'keep' ? undefined : 'red'}
              loading={deleteRows.isPending}
              onClick={() =>
                deleteRows.mutate({
                  filters: view.filters,
                  baseVersion: sheet.data?.version ?? 1,
                  keepMatching: cutting === 'keep',
                  name: cutDetails.name,
                  description: cutDetails.description,
                }, {
                  onSuccess: (made) => {
                    setCutting(null);
                    openNewFile(made, sheet.data?.file_name ?? 'The original');
                  },
                  onError: (e) =>
                    notifications.show({
                      color: 'red',
                      title:
                        cutting === 'keep'
                          ? 'Could not keep those rows'
                          : 'Could not remove those rows',
                      message: (e as Error).message,
                    }),
                })
              }
            >
              {cutting === 'keep' ? 'Save as new file' : 'Save the rest as a new file'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <SheetComputedModal
        sheetId={id}
        columns={columns}
        startFrom={computingFrom}
        baseVersion={sheet.data?.version ?? 1}
        opened={computingFrom !== null}
        onClose={() => setComputingFrom(null)}
      />

      <SheetFormulaModal
        sheetId={id}
        columns={columns}
        startFrom={formulaFrom}
        baseVersion={sheet.data?.version ?? 1}
        opened={formulaFrom !== null}
        onClose={() => setFormulaFrom(null)}
      />

      <SheetSplitModal
        sheetId={id}
        fileName={sheet.data?.file_name ?? ''}
        /* What is on screen, not what is in the file: splitting a filtered
           view into blocks of the whole file is how a buyer ends up with rows
           nobody meant to send them. */
        totalRows={total}
        filters={view.filters}
        opened={splitting}
        onClose={() => setSplitting(false)}
      />

      <SheetSuppressModal
        sheetId={id}
        fileName={sheet.data?.file_name ?? 'file'}
        columns={columns}
        baseVersion={sheet.data?.version ?? 1}
        opened={suppressing}
        onClose={() => setSuppressing(false)}
      />

      <SheetImportModal
        sheetId={id}
        fileName={sheet.data?.file_name ?? ''}
        columns={columns}
        /* What is on screen, the same as Split: an import of a filtered view
           is the rows that matched, never the whole file. */
        rowCount={total}
        filters={view.filters}
        filterSummary={view.filters.map(describeFilter).join(' and ')}
        defaultSupplierId={sheet.data?.supplier_id ?? null}
        opened={importing}
        onClose={() => setImporting(false)}
      />
    </Stack>
  );
}

/**
 * What a file is and where it came from, under its name: the description,
 * the file it was made from, and who made or uploaded it. The pencil renames
 * it or changes the description.
 */
function FileDetails({ sheet }: { sheet: NonNullable<ReturnType<typeof useSheet>['data']> }) {
  const words = useVocab();
  const update = useUpdateSheet(sheet.id);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const suppliers = useEntities('suppliers');
  const by = sheet.created_by ? (sheet.parent_sheet_id ? `Made by ${sheet.created_by}` : `Uploaded by ${sheet.created_by}`) : null;
  return (
    <>
      <Group gap={8} wrap="wrap" style={{ marginTop: -2 }}>
        <Text size="sm" c={sheet.description ? undefined : 'dimmed'} style={{ maxWidth: 640 }} lineClamp={2}>
          {sheet.description || 'No description yet.'}
        </Text>
        <Tooltip label={`Rename, describe or set the ${words.one('supplier')}`}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Rename or describe this file"
            onClick={() => {
              setName(sheet.file_name);
              setDescription(sheet.description ?? '');
              setSupplierId(sheet.supplier_id ?? null);
              setEditing(true);
            }}
          >
            <IconPencil size={14} />
          </ActionIcon>
        </Tooltip>
        {sheet.parent_sheet_id && (
          <Text size="xs" c="dimmed">
            Made from{' '}
            <Anchor component={Link} to={`/sheets/${sheet.parent_sheet_id}`} size="xs">
              {sheet.parent_name ?? 'a file that has since been removed'}
            </Anchor>
          </Text>
        )}
        <Text size="xs" c="dimmed">
          {sheet.supplier_name ? `From ${sheet.supplier_name}` : `No ${words.one('supplier')} set`}
        </Text>
        {by && <Text size="xs" c="dimmed">{by}</Text>}
      </Group>
      <Modal opened={editing} onClose={() => setEditing(false)} title="Edit file details" size="md">
        <Stack>
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Textarea
            label="Description"
            autosize
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          <Select
            label={words.One('supplier')}
            description="Who sent this file"
            placeholder={`No ${words.one('supplier')}`}
            searchable
            clearable
            data={(suppliers.data?.entities ?? [])
              .filter((sp) => sp.active || sp.id === sheet.supplier_id)
              .map((sp) => ({ value: sp.id, label: sp.name }))}
            value={supplierId}
            onChange={setSupplierId}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditing(false)}>Cancel</Button>
            <Button
              loading={update.isPending}
              disabled={!name.trim()}
              onClick={() =>
                update.mutate(
                  { file_name: name.trim(), description: description.trim(), supplier_id: supplierId ?? '' },
                  {
                    onSuccess: () => setEditing(false),
                    onError: (e) =>
                      notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
                  },
                )
              }
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
